// ============================================
// pages/FromApp.tsx
//
// Studio 分享落地页. URL: /community/from-app?intent=xxx&t=yyy
//
// 流程 (按 Forum-from-app-integration.md §0 顺序):
//   1. 解析 URL query
//   2. 调 community-share-redeem-intent (一次性兑换)
//   3. 调 community-bootstrap-session 建立 session
//   4. 调 community-check-publish-permission 检查家长授权
//   5. saveDraft + history.replaceState 抹 URL token
//   6. 跳 /publish
//
// 错误处理:
//   INTENT_NOT_FOUND / INTENT_EXPIRED / INTENT_CONSUMED → 友好错误页
//   BOOTSTRAP_WINDOW_EXPIRED 等 → 同上
//   家长未授权 → guardian-needed 视图 (Week 1 占位)
//   网络错误 → 重试按钮
// ============================================

import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { callApi } from '../services/api';
import { setInitialSession, isLoggedIn, getCachedUser } from '../services/auth';
import { saveDraft } from '../services/draft';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorPage from '../components/ErrorPage';
import type { IntentPayload, UserInfo } from '../types/community';

type State =
  | { phase: 'loading'; stage: string }
  | { phase: 'error'; code: string; message: string; canRetry: boolean };

export default function FromApp() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ phase: 'loading', stage: '正在加载分享…' });

  // useRef 防止 React StrictMode 双触发导致 intent 被消费两次
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const intentId = params.get('intent');
    const accessToken = params.get('t');

    if (!intentId || !accessToken) {
      setState({
        phase: 'error',
        code: 'INVALID_URL',
        message: '分享链接无效:缺少必要参数',
        canRetry: false,
      });
      return;
    }

    // 长度校验:intent_id = 'int_' + 16 hex = 20 字符;access_token = 32 hex
    if (intentId.length !== 20 || !intentId.startsWith('int_') || accessToken.length !== 32) {
      setState({
        phase: 'error',
        code: 'INVALID_URL',
        message: '分享链接格式错误',
        canRetry: false,
      });
      return;
    }

    handoff(intentId, accessToken).catch((e) => {
      console.error('[FromApp] unexpected error:', e);
      setState({
        phase: 'error',
        code: 'UNEXPECTED',
        message: '加载失败,请稍后重试',
        canRetry: true,
      });
    });
  }, [params]);

  async function handoff(intentId: string, accessToken: string) {
    // ── 1. 兑换 intent ──
    setState({ phase: 'loading', stage: '正在验证分享链接…' });
    const redeem = await callApi<{
      user_id: string;
      payload: IntentPayload;
      default_title: string;
      created_at: number;
    }>('community-share-redeem-intent', { intent_id: intentId, access_token: accessToken }, {
      noAuth: true,
    });

    if (!redeem.ok) {
      const code = String(redeem.error || 'REDEEM_FAILED');
      const msg =
        code === 'INTENT_NOT_FOUND' ? '分享链接无效'
        : code === 'INTENT_EXPIRED' ? '分享链接已过期(10 分钟有效)'
        : code === 'INTENT_CONSUMED' ? '分享链接已被使用过'
        : (redeem.message || '兑换失败');
      setState({ phase: 'error', code, message: msg, canRetry: false });
      return;
    }

    // 注意:redeem 后 intent 已被消费,刷新页面会失败
    // 所以下一步必须把 payload 保存到 sessionStorage

    // ── 2. 建立 session ──
    //   ⭐ 优化(2026-05-23)：已登录且就是分享者本人 → 跳过 bootstrap（token 同 secret 通用，
    //      profile 也早已存在）。仅未登录/非同账号才走 bootstrap SSO。省一次后端往返。
    const sharerUserId = String(redeem.user_id);
    const alreadyLoggedInAsSharer =
      isLoggedIn() && getCachedUser()?.user_id === sharerUserId;

    if (!alreadyLoggedInAsSharer) {
      setState({ phase: 'loading', stage: '正在登录…' });
      const boot = await callApi<{
        user_id: string;
        session_token: string;
        user_info: UserInfo;
      }>('community-bootstrap-session', { intent_id: intentId, access_token: accessToken }, {
        noAuth: true,
      });

      if (!boot.ok || !boot.session_token) {
        // SSO 失败. 但 payload 还可以救回来 — 保存草稿后引导用户登录.
        saveDraft({
          intent_id: intentId,
          access_token: accessToken,
          user_id: String(redeem.user_id),
          payload: redeem.payload as IntentPayload,
          default_title: String(redeem.default_title || ''),
          created_at: Number(redeem.created_at) || Date.now(),
          redeemed_at: Date.now(),
        });
        setState({
          phase: 'error',
          code: 'SSO_FAILED',
          message: '登录失败,请尝试手动登录后继续(作品已保存到草稿)',
          canRetry: true,
        });
        return;
      }

      setInitialSession(String(boot.session_token), (boot.user_info as UserInfo) || { user_id: String(boot.user_id) });
    }

    // ── 3. 保存草稿 ──
    saveDraft({
      intent_id: intentId,
      access_token: accessToken,
      user_id: String(redeem.user_id),
      payload: redeem.payload as IntentPayload,
      default_title: String(redeem.default_title || ''),
      created_at: Number(redeem.created_at) || Date.now(),
      redeemed_at: Date.now(),
    });

    // ── 4. 抹掉 URL 中的敏感参数 ──
    //   hash 路由下, query 在 #/from-app?intent=xxx&t=yyy.
    //   用 replaceState 把整个 hash 换成干净的 #/from-app (intent/t 不留在地址栏/历史).
    try {
      const cleanUrl = window.location.pathname + window.location.search + '#/from-app';
      window.history.replaceState({}, '', cleanUrl);
    } catch {
      // ignore
    }

    // ── 5. 检查发布闸(生日/年龄/家长授权) ──
    setState({ phase: 'loading', stage: '正在检查发布权限…' });
    const gate = await callApi<{ state: string; publish_enabled: boolean }>(
      'community-get-publish-gate',
    );

    if (!gate.ok) {
      // 查询失败不硬阻塞,继续走 publish(publish 页本身还会再校验)
      console.warn('[FromApp] gate 查询失败,放行:', gate.error);
      navigate('/publish', { replace: true });
      return;
    }

    // 按闸状态分流
    if (gate.state === 'need_birthday') {
      navigate('/birthday', { replace: true });
      return;
    }
    if (gate.state === 'need_guardian') {
      navigate('/guardian', { replace: true });
      return;
    }

    // ── 6. 放行,跳 publish ──
    navigate('/publish', { replace: true });
  }

  // ── UI ──
  if (state.phase === 'loading') {
    return <LoadingSpinner fullscreen hint={state.stage} />;
  }

  if (state.phase === 'error') {
    return (
      <ErrorPage
        title="分享链接无效"
        message={`${state.message}\n\n请回到 HiPiiDo Studio,重新点击「分享」按钮。`}
        code={state.code}
        onRetry={state.canRetry ? () => window.location.reload() : undefined}
      />
    );
  }

  return null;
}
